import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Coins, Shield, Star, Zap, Trophy, Gift } from 'lucide-react';
import confetti from 'canvas-confetti';
import { useSpring, animated } from '@react-spring/web';

interface DashboardHeaderProps {
  profile: {
    email?: string;
    name?: string;
    career_coins?: number;
    profile_completion?: number;
    total_experience?: number;
    mastered_skills?: string[];
    active_paths?: string[];
    network_size?: number;
  };
  progress?: {
    total_coins?: number;
    level?: number;
    xp?: number;
    total_experience?: number;
  };
}

export function DashboardHeader({ profile, progress }: DashboardHeaderProps) {
  const welcomeMessages = [
    {
      message: "justingmaguire! Our favorite awkward turtle's back! 🐢 Any new paths on the horizon?",
      avatar: "/avatars/turtle.png",
      theme: "from-green-500/20 to-teal-500/20",
      responses: [
        { 
          text: "Navigating uncharted waters, I am!", 
          reward: "Shell yeah! Let's see what you can uncover!",
          animation: "float",
          icon: "🚀"
        },
        { 
          text: "Just polishing my shell for the next adventure.", 
          reward: "That's the spirit! Your career is yours to command.",
          animation: "shine",
          icon: "✨"
        },
        { 
          text: "The horizon looks exciting today!", 
          reward: "Fantastic! We're ready for your next move – let's do this!",
          animation: "bounce",
          icon: "🌅"
        },
        { 
          text: "Let's uncover some hidden gems, shall we?", 
          reward: "Perfect! It looks like you know where you are heading!",
          animation: "sparkle",
          icon: "💎"
        }
      ]
    }
  ];

  const [selectedMessage] = useState(() => 
    welcomeMessages[Math.floor(Math.random() * welcomeMessages.length)]
  );
  const [selectedResponse, setSelectedResponse] = useState<string | null>(null);
  const [rewardCount, setRewardCount] = useState(0);
  const [isHovering, setIsHovering] = useState<number | null>(null);

  const coinSpring = useSpring({
    to: { scale: rewardCount ? 1.5 : 1 },
    from: { scale: 1 },
    reset: true,
  });

  const handleResponseClick = (reward: string, animation: string) => {
    setSelectedResponse(reward);
    setRewardCount(prev => prev + 1);
    celebrateReward();
    
    // Add special sound effect (if enabled)
    const audio = new Audio('/sounds/reward.mp3');
    audio.play().catch(() => {}); // Catch and ignore if audio is blocked
  };

  const animations = {
    float: {
      y: [0, -10, 0],
      transition: { duration: 2, repeat: Infinity }
    },
    shine: {
      rotate: [0, 360],
      scale: [1, 1.2, 1],
      transition: { duration: 1.5, repeat: Infinity }
    },
    bounce: {
      y: [0, -20, 0],
      transition: { duration: 0.5, repeat: 1 }
    },
    sparkle: {
      scale: [1, 1.4, 1],
      opacity: [1, 0.8, 1],
      transition: { duration: 0.3 }
    }
  };

  const celebrateReward = () => {
    const colors = ['#FFD700', '#FFA500', '#FF69B4', '#00FF00'];
    
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors
    });

    setTimeout(() => {
      confetti({
        particleCount: 50,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors
      });
      confetti({
        particleCount: 50,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors
      });
    }, 250);
  };

  return (
    <div className="space-y-6 pt-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`
          relative overflow-hidden rounded-xl 
          bg-gradient-to-r ${selectedMessage?.theme || 'from-blue-500/20 to-purple-500/20'}
          p-8 backdrop-blur-sm
        `}
      >
        {/* Animated background patterns */}
        <motion.div
          className="absolute inset-0 opacity-20"
          animate={{
            backgroundPosition: ['0% 0%', '100% 100%'],
            scale: [1, 1.1, 1]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse"
          }}
          style={{
            backgroundImage: 'url("/patterns/dots.svg")',
            backgroundSize: '20px 20px'
          }}
        />

        {/* Message Content */}
        <div className="relative z-10 flex items-center gap-6">
          <motion.div
            whileHover={{ scale: 1.1, rotate: [0, -10, 10, 0] }}
            className="shrink-0 relative"
          >
            <img
              src={selectedMessage.avatar}
              alt="Avatar"
              className="w-24 h-24 rounded-full border-4 border-white/50 shadow-xl"
            />
            <motion.div
              className="absolute -top-2 -right-2 bg-yellow-400 rounded-full p-2"
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            >
              <Trophy className="w-4 h-4 text-white" />
            </motion.div>
          </motion.div>

          <div className="flex-1 space-y-4">
            <motion.h1
              className="text-2xl md:text-3xl font-bold text-primary-900"
            >
              {selectedMessage.message}
            </motion.h1>

            {/* Response Options */}
            {selectedMessage?.responses && (
              <AnimatePresence>
                {!selectedResponse && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="grid grid-cols-1 md:grid-cols-2 gap-3"
                  >
                    {selectedMessage.responses.map((response, index) => (
                      <motion.button
                        key={index}
                        whileHover={{ 
                          scale: 1.02, 
                          backgroundColor: 'rgba(255,255,255,0.3)',
                          boxShadow: '0 0 20px rgba(255,255,255,0.5)'
                        }}
                        whileTap={{ scale: 0.98 }}
                        onHoverStart={() => setIsHovering(index)}
                        onHoverEnd={() => setIsHovering(null)}
                        className="p-4 rounded-lg bg-white/20 backdrop-blur-sm 
                                 text-left text-primary-900 hover:shadow-lg transition-all
                                 flex items-center gap-3 group"
                        onClick={() => handleResponseClick(response.reward, response.animation)}
                      >
                        <motion.span
                          animate={isHovering === index ? animations[response.animation] : {}}
                          className="text-2xl"
                        >
                          {response.icon}
                        </motion.span>
                        <span className="flex-1">{response.text}</span>
                        <motion.div
                          className="opacity-0 group-hover:opacity-100"
                          animate={{ x: isHovering === index ? [0, 5, 0] : 0 }}
                          transition={{ repeat: Infinity }}
                        >
                          <Zap className="w-5 h-5 text-primary-600" />
                        </motion.div>
                      </motion.button>
                    ))}
                  </motion.div>
                )}

                {/* Reward Message */}
                {selectedResponse && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex items-center gap-2 text-primary-900 p-4 bg-white/30 rounded-lg backdrop-blur-sm"
                  >
                    <Sparkles className="h-5 w-5" />
                    <span className="font-medium">{selectedResponse}</span>
                    <animated.div style={coinSpring} className="flex items-center gap-1 ml-2 px-3 py-1 bg-yellow-400/20 rounded-full">
                      <Coins className="h-4 w-4 text-yellow-600" />
                      <span className="text-sm font-bold text-yellow-600">+1</span>
                    </animated.div>
                  </motion.div>
                )}
              </AnimatePresence>
            )}
          </div>
        </div>

        {/* Achievement Banner */}
        {rewardCount > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute bottom-4 right-4 bg-white/30 backdrop-blur-sm px-4 py-2 rounded-full
                     flex items-center gap-2 text-primary-900"
          >
            <Gift className="h-4 w-4" />
            <span className="font-medium">Daily Streak: {rewardCount}</span>
            <Star className="h-4 w-4 text-yellow-500" />
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}